<template>
  <div>
    <q-input
      ref="qInput"
      v-bind="propsAndAttrs"
      :debounce="debounce"
      outlined
      :maxlength="max || maxLength"
      dense
      class="tm-field-text"
      :type="inputType"
      :class="[
        fieldClass,
        {
          'tm-field-text--disabled': isDisabled,
          'tm-field-text--blocked': isBlocked,
          'tm-field-text--focused': isFocused,
          'tm-field-text--clearable': clearable && !!modelValue,
          'tm-field-text--flat': flat,
          [`tm-field-text--${kebabSize}`]: size,
          'tm-field-text--transparent': transparent,
          'tm-field-text--small-padding': smallPadding,
          'tm-field-text--no-append-padding': noAppendPadding,
          'tm-field-text--rounded': rounded,
        },
      ]"
      :tabindex="frozen ? '-1' : ''"
      :disable="isDisabled || isBlocked"
      :model-value="modelValue || ''"
      :data-lpignore="enableLastPass ? undefined : 'true'"
      :clearable="clearable"
      hide-bottom-space
      @update:model-value="onInput"
      @change="emitChange"
      @blur="onBlur"
      @paste="handlePaste"
      @clear="$emit('clear')"
    >
      <template
        v-if="leftIcon || $slots.prepend"
        v-slot:prepend
      >
        <slot name="prepend">
          <tm-icon
            :name="leftIcon!"
            size="xLarge"
          />
        </slot>
      </template>

      <template
        v-if="domainText || rightIcon || $slots.append"
        v-slot:append
      >
        <slot name="append">
          <tm-icon
            v-if="rightIcon"
            :name="rightIcon"
            size="xLarge"
          />
          <field-text-domain
            v-else-if="domainText"
            :domain-text="domainText"
            background-color="white"
          />
        </slot>
      </template>
    </q-input>

    <!-- Links don't work in QInput's hints -->
    <div
      v-if="!hideMessage && (innerErrorState || hint || $slots.hint)"
      class="field-text__bottom"
    >
      <div
        v-if="innerErrorState"
        class="tm-field-text--error"
      >
        <slot name="error">
          <tm-error
            :field="name ?? 'text'"
            :errors="errorsBag"
            :server-errors="serverErrors"
            :invalid="innerErrorState"
          />
        </slot>
      </div>

      <slot name="hint">
        {{ hint }}
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { kebabCase } from 'lodash-es'
import type { QInput, QInputProps } from 'quasar'
import {
  defineComponent,
  ref,
  type PropType,
  computed,
  nextTick,
  onUnmounted,
  watch,
} from '@/composition/vue/compositionApi'
import { fieldProps } from '@/components/forms/fieldBase'
import { useField } from '@/composition/forms/useField'
import { fieldEmits } from '@/services/forms/types'
import TmIcon from '@/components/shared/TmIcon.vue'
import type { SizeProp } from '@/components/shared/types'
import { useFieldInputRefActions } from '@/composition/forms/fields/fieldText'
import type { IconName } from '@/assets/icons/icons'
import { useFormInputCallback } from '@/composition/forms'
import { processPasteTextToInput } from '@/utils/processPasteTextToInput'
import { useStatelessFormControl } from '@/composition/forms/useStatelessFormControl'
import type { IControl } from '@/composition/forms/types'
import TmError from '@/components/forms/TmError.vue'
import FieldTextDomain from '@/components/forms/fields/FieldTextDomain.vue'

type Value = string
export type FieldTextSize = 'xSmall' | 'small' | 'medium' | 'large' | ''

export default defineComponent({
  components: {
    FieldTextDomain,
    TmIcon,
    TmError,
  },
  props: {
    ...fieldProps,
    modelValue: {
      type: String,
      required: true,
    },
    leftIcon: {
      type: String as PropType<IconName>,
    },
    rightIcon: {
      type: String as PropType<IconName>,
    },
    fieldClass: {
      type: [String, Object],
      default: '',
    },
    autoselect: {
      type: Boolean,
      default: false,
    },
    errorState: {
      type: Boolean,
      default: false,
    },
    domainText: {
      type: String,
    },
    size: {
      type: String as SizeProp<FieldTextSize>,
      default: '',
    },
    debounce: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
    },
    maxLength: {
      type: Number,
      default: 255,
    },
    enableLastPass: {
      type: Boolean,
    },
    trim: {
      type: Boolean,
      default: false,
    },
    frozen: {
      type: Boolean,
    },
    inputClass: {
      type: String,
    },
    pasteHandler: {
      type: Function as PropType<(value: string) => string>,
    },
    smallPadding: {
      type: Boolean,
    },
    noAppendPadding: {
      type: Boolean,
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
    },
    isDisabledPasteHandler: {
      type: Boolean,
    },
    hideMessage: {
      type: Boolean,
    },
    nativeType: {
      type: String as PropType<QInputProps['type']>,
    },
    rounded: {
      type: Boolean,
    },
  },
  emits: {
    ...fieldEmits<Value>(),
    clear: () => true,
    paste: (_: string) => true,
    'typing-start': () => true,
    blur: (_: FocusEvent) => true,
  },
  setup(props, context) {
    const qInput = ref<QInput>()
    const { propsAndAttrs, emitInput, emitChange } = useField<Value>(props, context, ['class'])

    let control: IControl | null = null
    if (props.isStateless) {
      const data = useStatelessFormControl({
        reset: () => emitInput(''),
      })
      control = data.control
    }

    const errorsBag = computed(() => control?.getErrors() ?? props.errors)
    const innerErrorState = computed(
      () => props.errorState || errorsBag.value.length > 0 || props.serverErrors.length > 0,
    )

    const kebabSize = computed(() => kebabCase(props.size))

    const { focus, blur } = useFieldInputRefActions(() => qInput.value)
    useFormInputCallback({ focus })

    const onInput = (value: string | number | null) => {
      if (value == null) {
        value = ''
      }

      emitInput(String(value))

      if (qInput.value) {
        focus()
      }
      handleTypingEnd()
    }
    const onBlur = (e: Event) => {
      context.emit('blur', e as FocusEvent)
      if (props.trim) {
        emitInput(props.modelValue.trim())
      }
      if (props.isStateless) {
        control?.handleBlur()
      }
    }

    const nativeEl = computed(() => qInput.value?.nativeEl)

    // Typing state management for immediate typing detection
    const isTyping = ref(false)
    let typingStartListener: ((event: Event) => void) | null = null

    const handleTypingStart = () => {
      if (!isTyping.value) {
        isTyping.value = true
        context.emit('typing-start')
      }
    }

    const handleTypingEnd = () => {
      isTyping.value = false
    }

    // Setup native input event listeners for immediate typing detection
    watch(
      nativeEl,
      (newNativeEl, oldNativeEl) => {
        // Remove listener from old element if exists
        if (oldNativeEl && typingStartListener) {
          oldNativeEl.removeEventListener('input', typingStartListener)
        }

        // Add listener to new element if exists
        if (newNativeEl) {
          typingStartListener = handleTypingStart
          newNativeEl.addEventListener('input', typingStartListener)
        }
      },
      { immediate: true },
    )

    onUnmounted(() => {
      if (nativeEl.value && typingStartListener) {
        nativeEl.value.removeEventListener('input', typingStartListener)
      }
    })

    const handlePaste = (pasteEvent: ClipboardEvent) => {
      if (props.isDisabledPasteHandler) {
        return
      }
      if (!qInput.value?.nativeEl) {
        return
      }

      /**
       * Only the provided input types supports manipulations with selection.
       * Other types doesn't provide selectionStart, selectionEnd and setSelectionRange properties.
       * So we're just skipping unsupported input types.
       *
       * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
       * @see https://html.spec.whatwg.org/multipage/input.html#input-type-attr-summary
       */
      if (!['text', 'search', 'tel', 'url', 'password'].includes(qInput.value.nativeEl.type)) {
        return
      }

      pasteEvent.preventDefault()

      const textFromClipboard = pasteEvent.clipboardData?.getData('text') || ''
      const modelValue = props.modelValue || ''

      const { newValue, cursorPosition } = processPasteTextToInput(
        qInput.value!.nativeEl as HTMLInputElement,
        textFromClipboard,
        modelValue,
        props.maxLength,
      )

      let prepareValue: string
      if (props.pasteHandler) {
        prepareValue = props.pasteHandler(newValue)
      } else {
        prepareValue = newValue
      }

      onInput(newValue)
      context.emit('paste', prepareValue)

      nextTick(() => {
        qInput.value?.nativeEl.setSelectionRange(cursorPosition, cursorPosition)
      })
    }

    const inputType = computed(() => {
      return props.nativeType ?? propsAndAttrs.value.type
    })

    return {
      qInput,
      onInput,
      emitChange,
      propsAndAttrs,
      kebabSize,
      focus,
      blur,
      onBlur,
      nativeEl,
      handlePaste,
      errorsBag,
      innerErrorState,
      isTyping,
      inputType,
    }
  },
})
</script>

<style lang="scss" scoped>
@use '@/styles/fields/const' as fieldConst;

@import '@/styles/quasar-field.scss';
@import '@/styles/mixins.scss';

$paddingX: 13px;

.field-text {
  &__bottom {
    margin-top: 4px;
    color: $gray6;
    font-size: $sm-font;
    font-weight: $regular-font;
    line-height: 16px;
    letter-spacing: normal;
  }
}

.tm-field-text {
  @include quasar-field;

  :deep(.q-field__control) {
    .q-field__append .q-spinner {
      margin-right: $paddingX;
    }

    .q-field__suffix {
      padding-right: 16px;
    }
  }

  &--rounded {
    :deep(.q-field__control) {
      border-radius: 66px !important;
    }
  }

  &--transparent {
    &:not(.q-field--focused) {
      &:hover {
        :deep(.q-field__control) {
          input {
            cursor: pointer;
          }
        }
      }

      :deep(.q-field__control) {
        input {
          text-overflow: ellipsis;
        }
      }
    }
  }

  &--focused#{&}--clearable :deep(.q-field__native) {
    padding-right: 0 !important;
  }

  &--error {
    margin-bottom: 4px;
    font-size: $sm-font;
    font-weight: $regular-font;
    line-height: 14px;
    color: $error;
  }

  &--disabled {
    pointer-events: none;
  }

  &--blocked {
    :deep(.q-field__control) {
      opacity: 1 !important;
      pointer-events: auto;

      &::before {
        pointer-events: auto;
      }
    }
  }

  &--small-padding {
    :deep(.q-field__control) {
      input {
        padding: 0 8px !important;
      }
    }
  }

  // default style
  :deep(.q-field__control) {
    // right icon position
    @include field-right-icon();
    // clear button
    @include field-right-icon-cancel();
    input {
      padding: var(--field-text-control-padding, 0 $paddingX);
    }
    i {
      color: $gray !important;
      &.success--text {
        color: $success !important;
      }
      &.light--text {
        color: $light !important;
      }
    }
    // left icon position
    .q-field__prepend {
      margin-right: -5px;
      padding-right: 0;
    }
    .q-field__marginal {
      height: 100%;
    }

    .q-field__native {
      text-overflow: ellipsis;
    }
  }

  &--no-append-padding {
    :deep(.q-field__control) {
      .q-field__append {
        margin-right: 0 !important;
      }
    }
  }

  //fieldText-sizes (name, height, font-size, left icon margin-left, icon font-size)
  $fieldText-sizes:
    'x-small' 28px $sm-font 7px 15px,
    'small' 32px $font-size-root 12px 16px,
    'medium' 36px $font-size-root 12px 18px,
    '' 40px $font-size-root 11px 20px,
    'auto' auto $font-size-root 11px 20px;

  @mixin fieldText-style($height, $font-size, $margin-left, $i-font-size) {
    :deep(.q-field__control) {
      height: $height;
      // left icon position
      .q-field__prepend {
        margin-left: $margin-left;
        height: $height;
      }
      input {
        font-size: var(--field-text-font-size, $font-size) !important;
      }
    }
  }

  @each $name, $height, $font-size, $margin-left, $i-font-size in $fieldText-sizes {
    & {
      @if $name == '' {
        @include fieldText-style($height, $font-size, $margin-left, $i-font-size);
      } @else {
        &.tm-field-text--#{$name} {
          @include fieldText-style($height, $font-size, $margin-left, $i-font-size);
        }
      }
    }
  }
}

.tm-field-text--small {
  :deep(.q-field__marginal) {
    font-size: 16px;
  }
}
</style>
